import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Shield, Smartphone, Key, CheckCircle, Copy, AlertCircle, Loader, ArrowRight } from 'lucide-react';
import axios from 'axios';

const TwoFactorSetupPage = () => {
  const { user, getAuthHeaders, logout } = useAuth();
  const navigate = useNavigate();
  const [step, setStep] = useState('intro'); // intro, setup, verify, backup, complete
  const [loading, setLoading] = useState(false);
  const [setupData, setSetupData] = useState(null);
  const [verifyCode, setVerifyCode] = useState('');
  const [error, setError] = useState('');
  const [copiedCodes, setCopiedCodes] = useState(false);

  const headers = getAuthHeaders();
  const API = process.env.REACT_APP_BACKEND_URL + '/api';

  useEffect(() => {
    // Check if user already has 2FA
    checkStatus();
  }, []);

  const checkStatus = async () => {
    try {
      const res = await axios.get(`${API}/2fa/status`, { headers });
      if (res.data.enabled) {
        // Already enrolled, redirect to portal
        navigate(user?.role === 'admin' ? '/admin' : '/portal');
      }
    } catch (err) {
      console.error('Error checking 2FA status:', err);
    }
  };

  const startSetup = async () => {
    setLoading(true);
    setError('');
    try {
      const res = await axios.post(`${API}/2fa/setup`, {}, { headers });
      setSetupData(res.data);
      setStep('setup');
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to initialize 2FA setup');
    } finally {
      setLoading(false);
    }
  };

  const verifySetup = async () => {
    if (verifyCode.length !== 6) {
      setError('Please enter a 6-digit code');
      return;
    }
    
    setLoading(true);
    setError('');
    try {
      await axios.post(`${API}/2fa/verify-setup`, { code: verifyCode }, { headers });
      setStep('backup');
    } catch (err) {
      setError(err.response?.data?.detail || 'Invalid code. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const copyBackupCodes = () => {
    const codes = setupData?.backup_codes?.join('\n');
    navigator.clipboard.writeText(codes);
    setCopiedCodes(true);
    setTimeout(() => setCopiedCodes(false), 3000);
  };

  const completeSetup = () => {
    navigate(user?.role === 'admin' ? '/admin' : '/portal');
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-6" style={{ background: 'linear-gradient(135deg, #0B1F3A 0%, #1a3a5c 100%)' }}>
      <div className="bg-white rounded-3xl shadow-2xl max-w-md w-full overflow-hidden">
        {/* Header */}
        <div className="p-6 text-center border-b border-gray-100">
          <div className="w-16 h-16 rounded-2xl bg-[#0B1F3A] flex items-center justify-center mx-auto mb-4">
            <Shield size={32} className="text-white" />
          </div>
          <h1 className="font-heading text-xl font-bold text-[#0B1F3A]">Two-Factor Authentication</h1>
          <p className="text-[#475569] text-sm mt-1">Required for account security</p>
        </div>

        <div className="p-6">
          {error && (
            <div className="flex items-center gap-2 p-3 mb-4 bg-red-50 border border-red-200 rounded-xl text-red-700 text-sm">
              <AlertCircle size={18} /> {error}
            </div>
          )}

          {/* Step: Intro */}
          {step === 'intro' && (
            <div className="text-center">
              <p className="text-[#475569] mb-6">
                To protect your account and sensitive tax information, two-factor authentication is required for all users.
              </p>
              
              <div className="bg-gray-50 rounded-xl p-4 mb-6 text-left">
                <h3 className="font-semibold text-[#0B1F3A] mb-3">You'll need:</h3>
                <ul className="space-y-2 text-sm text-[#475569]">
                  <li className="flex items-center gap-2">
                    <Smartphone size={16} className="text-[#0B1F3A]" />
                    An authenticator app (Google Authenticator, Authy, etc.)
                  </li>
                  <li className="flex items-center gap-2">
                    <Key size={16} className="text-[#0B1F3A]" />
                    A secure place to store backup codes
                  </li>
                </ul>
              </div>

              <button
                onClick={startSetup}
                disabled={loading}
                className="flex items-center justify-center gap-2 w-full py-3.5 rounded-xl font-semibold text-white disabled:opacity-60"
                style={{ background: '#0B1F3A' }}
              >
                {loading ? <Loader size={18} className="animate-spin" /> : <ArrowRight size={18} />}
                Get Started
              </button>
            </div>
          )}

          {/* Step: Setup (QR Code) */}
          {step === 'setup' && setupData && (
            <div>
              <p className="text-[#475569] text-sm mb-4 text-center">
                Scan this QR code with your authenticator app:
              </p>
              
              <div className="flex justify-center mb-4">
                <img src={setupData.qr_code} alt="QR Code" className="w-48 h-48 rounded-xl border border-gray-200" />
              </div>

              <div className="bg-gray-50 rounded-xl p-3 mb-4">
                <p className="text-xs text-[#94A3B8] mb-1">Or enter this code manually:</p>
                <code className="text-sm font-mono text-[#0B1F3A] break-all">{setupData.manual_entry_key}</code>
              </div>

              <div className="mb-4">
                <label className="block text-sm font-medium text-[#475569] mb-2">
                  Enter the 6-digit code from your app:
                </label>
                <input
                  type="text"
                  value={verifyCode}
                  onChange={(e) => setVerifyCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  className="w-full px-4 py-3 rounded-xl border border-gray-200 text-center text-2xl font-mono tracking-widest"
                  placeholder="000000"
                  maxLength={6}
                />
              </div>

              <button
                onClick={verifySetup}
                disabled={loading || verifyCode.length !== 6}
                className="flex items-center justify-center gap-2 w-full py-3.5 rounded-xl font-semibold text-white disabled:opacity-60"
                style={{ background: '#0B1F3A' }}
              >
                {loading ? <Loader size={18} className="animate-spin" /> : <CheckCircle size={18} />}
                Verify & Continue
              </button>
            </div>
          )}

          {/* Step: Backup Codes */}
          {step === 'backup' && setupData && (
            <div>
              <div className="flex items-center gap-2 p-3 mb-4 bg-amber-50 border border-amber-200 rounded-xl text-amber-700 text-sm">
                <AlertCircle size={18} />
                Save these codes! They're your only way to access your account if you lose your device.
              </div>

              <div className="bg-gray-50 rounded-xl p-4 mb-4">
                <div className="grid grid-cols-2 gap-2">
                  {setupData.backup_codes?.map((code, i) => (
                    <div key={i} className="font-mono text-sm text-[#0B1F3A] bg-white px-3 py-2 rounded border border-gray-200">
                      {code}
                    </div>
                  ))}
                </div>
              </div>

              <button
                onClick={copyBackupCodes}
                className="flex items-center justify-center gap-2 w-full py-2.5 rounded-xl font-semibold text-[#475569] border border-gray-200 mb-3"
              >
                <Copy size={16} />
                {copiedCodes ? 'Copied!' : 'Copy All Codes'}
              </button>

              <button
                onClick={completeSetup}
                className="flex items-center justify-center gap-2 w-full py-3.5 rounded-xl font-semibold text-white"
                style={{ background: '#10B981' }}
              >
                <CheckCircle size={18} />
                I've Saved My Codes - Continue
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default TwoFactorSetupPage;
